import { forwardRef } from "react";

import { Box, Stack, StackProps, useTheme } from "@mui/material";
import { AvailablePaymentCurrency, formatCurrencyAmount, getDollarValue } from "@simplyk/common";

import { useLocaleContext } from "@/contexts/LocaleContext";
import { getDate } from "@/forms-design-system/FormDate/helper";
import { FormDivider } from "@/forms-design-system/FormDivider";
import { FormTypography } from "@/forms-design-system/FormTypography";
import { InvalidThemeErrorMessage } from "@/forms-design-system/InvalidThemeErrorMessage";
import { mergeSx } from "@/helpers/mergeSx";
import { useTranslate } from "@/hooks/useTranslate";

type FormPaymentSummaryProps = Omit<StackProps, "children"> & {
  total: number;
  currency: AvailablePaymentCurrency;
  renewalDate?: Date | number | null;
  slots: {
    order: React.ReactNode;
    tip?: React.ReactNode;
  };
};

export const FormPaymentSummary = forwardRef<HTMLDivElement, FormPaymentSummaryProps>(
  ({ total, slots, currency, renewalDate, sx, ...rest }, ref) => {
    const theme = useTheme();
    const { t } = useTranslate();
    const { isoLocale } = useLocaleContext();

    if (!theme.palette.forms || !theme.palette.formsComponents?.paymentSummary) {
      return <InvalidThemeErrorMessage />;
    }

    return (
      <Box
        ref={ref}
        {...rest}
        sx={mergeSx(
          {
            color: theme.palette.formsComponents.paymentSummary.textColor,
            backgroundColor: theme.palette.formsComponents.paymentSummary.backgroundColor,
            border: `1px solid ${theme.palette.formsComponents.paymentSummary.borderColor}`,
            borderRadius: theme.radius(1),
          },
          sx
        )}
      >
        <Stack gap={3} padding={theme.spacing(3, 2)}>
          <Box>{slots.order}</Box>
          {slots.tip && (
            <>
              <FormDivider />
              <Box>{slots.tip}</Box>
            </>
          )}
        </Stack>
        <Stack
          sx={{
            padding: 2,
            backgroundColor: theme.palette.formsComponents.paymentSummary.totalSectionBackgroundColor,
            borderEndStartRadius: theme.radius(1),
            borderEndEndRadius: theme.radius(1),
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2}>
            <FormTypography
              color={theme.palette.formsComponents.paymentSummary.totalSectionTextColor}
              variant="subtitle1"
            >
              {t("common", "paymentSummary.total")}
            </FormTypography>
            <Box sx={{ textAlign: "end" }}>
              <FormTypography
                component="div"
                color={theme.palette.formsComponents.paymentSummary.totalSectionTextColor}
                variant="subtitle1"
                data-test="payment-processor-total-with-tip-amount"
              >
                {formatCurrencyAmount({
                  value: getDollarValue(total),
                  locale: isoLocale,
                  currency,
                  options: {
                    currencyDisplay: "code",
                  },
                })}
                {renewalDate && (
                  <FormTypography
                    component="div"
                    variant="caption"
                    color={theme.palette.formsComponents.paymentSummary.renewalDateTextColor}
                    sx={{ textAlign: "right" }}
                    data-test="payment-processor-total-with-tip-renew-date"
                  >
                    {t("common", "paymentSummary.nextRenewal", {
                      renewalDate: getDate(renewalDate).toLocaleString(
                        {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        },
                        { locale: isoLocale }
                      ),
                    })}
                  </FormTypography>
                )}
              </FormTypography>
            </Box>
          </Stack>
        </Stack>
      </Box>
    );
  }
);

FormPaymentSummary.displayName = "FormPaymentSummary";
